/**
 * Tabs Component - Bootstrap 5 Compatible
 * 
 * 实现完整的标签页功能，包括：
 * - 自动初始化
 * - 键盘导航支持
 * - 动画过渡
 * - 事件系统
 * - 历史记录支持（可选）
 * - 响应式行为
 * 
 * @author Iatrode UI Team
 * @version 1.0.0
 */

(function() {
  'use strict';

  // 默认配置
  const defaults = {
    animation: true,
    animationDuration: 300,
    keyboard: true,
    history: false,
    hashPrefix: 'tab-',
    onShow: null,
    onShown: null,
    onHide: null,
    onHidden: null
  };

  /**
   * Tab 类
   */
  class Tab {
    constructor(element, options) {
      this.element = element;
      this.options = { ...defaults, ...options };
      this.isTransitioning = false;
      
      // 获取相关元素
      this.tabList = element.closest('.nav');
      this.targetSelector = element.getAttribute('data-bs-target') || 
                           element.getAttribute('href');
      this.target = this.targetSelector ? 
                    document.querySelector(this.targetSelector) : null;
      
      // 保存实例引用
      this.element._tabInstance = this;
      
      this.init();
    }

    init() {
      // 绑定点击事件（使用箭头函数保持this上下文）
      this.clickHandler = (e) => {
        e.preventDefault();
        this.show();
      };
      this.element.addEventListener('click', this.clickHandler);

      // 键盘导航
      if (this.options.keyboard) {
        this.element.addEventListener('keydown', this.handleKeydown.bind(this));
      }

      // 历史记录支持
      if (this.options.history) {
        this.setupHistory();
      }

      // 设置 ARIA 属性
      this.setupAccessibility();
    }

    show() {
      if (this.isTransitioning) return;
      if (this.element.classList.contains('active')) return;
      if (this.element.classList.contains('disabled') || 
          this.element.hasAttribute('disabled')) return;

      const previous = this.tabList.querySelector('.nav-link.active');
      const previousPane = previous ? 
        document.querySelector(
          previous.getAttribute('data-bs-target') || 
          previous.getAttribute('href')
        ) : null;

      // 触发 hide 事件
      if (previous) {
        const hideEvent = new CustomEvent('hide.bs.tab', {
          bubbles: true,
          cancelable: true,
          detail: { relatedTarget: this.element }
        });
        previous.dispatchEvent(hideEvent);
        if (hideEvent.defaultPrevented) return;
      }

      // 触发 show 事件
      const showEvent = new CustomEvent('show.bs.tab', {
        bubbles: true,
        cancelable: true,
        detail: { relatedTarget: previous }
      });
      this.element.dispatchEvent(showEvent);
      if (showEvent.defaultPrevented) return;

      // 执行回调
      if (this.options.onShow) {
        this.options.onShow.call(this.element, previous);
      }

      // 开始过渡
      this.isTransitioning = true;

      // 隐藏当前活动的面板
      if (previousPane) {
        this.hidePane(previousPane, () => {
          // 隐藏完成后显示新面板
          this.showPane();
        });
      } else {
        this.showPane();
      }

      // 更新历史记录
      if (this.options.history && this.targetSelector) {
        const hash = this.options.hashPrefix + this.targetSelector.replace('#', '');
        window.history.pushState(null, '', '#' + hash);
      }
    }

    showPane() {
      // 移除其他 tab 的 active 状态
      const allTabs = this.tabList.querySelectorAll('.nav-link');
      allTabs.forEach(tab => {
        tab.classList.remove('active');
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '-1');
      });

      // 激活当前 tab
      this.element.classList.add('active');
      this.element.setAttribute('aria-selected', 'true');
      this.element.removeAttribute('tabindex');
      
      // 添加光效动画类
      this.element.classList.add('tab-shimmer-play');
      
      // 0.75s 后移除光效类，与动画时间同步
      setTimeout(() => {
        this.element.classList.remove('tab-shimmer-play');
      }, 750);

      if (!this.target) {
        this.isTransitioning = false;
        return;
      }

      // 隐藏所有面板
      const tabContent = this.target.closest('.tab-content');
      if (tabContent) {
        const allPanes = tabContent.querySelectorAll('.tab-pane');
        allPanes.forEach(pane => {
          pane.classList.remove('show', 'active');
        });
      }

      // 显示目标面板
      if (this.options.animation) {
        this.target.classList.add('fade');
        
        // 强制重排以触发动画
        this.target.offsetHeight; // eslint-disable-line no-unused-expressions
        
        this.target.classList.add('show', 'active');

        setTimeout(() => {
          this.isTransitioning = false;
          this.onShown();
        }, this.options.animationDuration);
      } else {
        this.target.classList.add('show', 'active');
        this.isTransitioning = false;
        this.onShown();
      }
    }

    hidePane(pane, callback) {
      if (this.options.animation) {
        pane.classList.remove('show');
        
        setTimeout(() => {
          pane.classList.remove('active');
          if (callback) callback();
        }, this.options.animationDuration);
      } else {
        pane.classList.remove('show', 'active');
        if (callback) callback();
      }

      // 触发 hidden 事件
      const hiddenEvent = new CustomEvent('hidden.bs.tab', {
        bubbles: true,
        detail: { relatedTarget: this.element }
      });
      
      const previousTab = this.tabList.querySelector(
        `[data-bs-target="${pane.id}"], [href="#${pane.id}"]`
      );
      
      if (previousTab) {
        previousTab.dispatchEvent(hiddenEvent);
      }

      // 执行回调
      if (this.options.onHidden) {
        this.options.onHidden.call(previousTab, this.element);
      }
    }

    onShown() {
      // 触发 shown 事件
      const shownEvent = new CustomEvent('shown.bs.tab', {
        bubbles: true
      });
      this.element.dispatchEvent(shownEvent);

      // 执行回调
      if (this.options.onShown) {
        this.options.onShown.call(this.element);
      }
    }

    handleKeydown(e) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const tabs = Array.from(this.tabList.querySelectorAll(
        '.nav-link:not(.disabled):not([disabled])'
      ));
      const currentIndex = tabs.indexOf(this.element);

      let nextIndex;
      switch (e.key) {
        case 'ArrowLeft':
          nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
          break;
        case 'ArrowRight':
          nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
      }

      const nextTab = tabs[nextIndex];
      if (nextTab) {
        nextTab.focus();
        // 创建 Tab 实例并显示
        const tabInstance = Tab.getInstance(nextTab) || new Tab(nextTab);
        tabInstance.show();
      }
    }

    setupAccessibility() {
      // 设置 role
      if (!this.element.hasAttribute('role')) {
        this.element.setAttribute('role', 'tab');
      }

      // 设置 aria-selected
      if (this.element.classList.contains('active')) {
        this.element.setAttribute('aria-selected', 'true');
      } else {
        this.element.setAttribute('aria-selected', 'false');
        this.element.setAttribute('tabindex', '-1');
      }

      // 设置 aria-controls
      if (this.target && !this.element.hasAttribute('aria-controls')) {
        this.element.setAttribute('aria-controls', this.target.id);
      }

      // 设置 tablist role
      if (this.tabList && !this.tabList.hasAttribute('role')) {
        this.tabList.setAttribute('role', 'tablist');
      }

      // 设置 tabpanel role
      if (this.target && !this.target.hasAttribute('role')) {
        this.target.setAttribute('role', 'tabpanel');
      }
    }

    setupHistory() {
      // 监听 popstate 事件
      window.addEventListener('popstate', () => {
        this.checkHash();
      });

      // 检查初始 hash
      this.checkHash();
    }

    checkHash() {
      const hash = window.location.hash.slice(1);
      if (hash.startsWith(this.options.hashPrefix)) {
        const targetId = '#' + hash.replace(this.options.hashPrefix, '');
        const tab = this.tabList.querySelector(
          `[data-bs-target="${targetId}"], [href="${targetId}"]`
        );
        if (tab) {
          const tabInstance = Tab.getInstance(tab) || new Tab(tab, this.options);
          tabInstance.show();
        }
      }
    }

    dispose() {
      if (this.clickHandler) {
        this.element.removeEventListener('click', this.clickHandler);
      }
      if (this.element) {
        delete this.element._tabInstance;
      }
      this.element = null;
      this.tabList = null;
      this.target = null;
      this.clickHandler = null;
    }

    // 静态方法
    static getInstance(element) {
      return element._tabInstance || null;
    }

    static getOrCreateInstance(element, options) {
      return element._tabInstance || new Tab(element, options);
    }
  }

  /**
   * 自动初始化
   */
  function initTabs() {
    const tabElements = document.querySelectorAll(
      '[data-bs-toggle="tab"], [data-bs-toggle="pill"]'
    );
    
    tabElements.forEach(element => {
      // 跳过已初始化的元素
      if (element._tabInstance) {
        return;
      }
      
      const options = {};
      
      // 读取 data 属性
      if (element.hasAttribute('data-bs-animation')) {
        options.animation = element.getAttribute('data-bs-animation') !== 'false';
      }
      if (element.hasAttribute('data-bs-keyboard')) {
        options.keyboard = element.getAttribute('data-bs-keyboard') !== 'false';
      }
      if (element.hasAttribute('data-bs-history')) {
        options.history = element.getAttribute('data-bs-history') === 'true';
      }

      // 创建Tab实例（会自动保存到element._tabInstance）
      new Tab(element, options);
    });
    
    console.log('Tabs initialized:', tabElements.length, 'tab elements found');
  }

  /**
   * 响应式行为：移动端水平滚动优化
   */
  function setupResponsive() {
    const tabLists = document.querySelectorAll('.nav-tabs, .nav-pills');
    
    tabLists.forEach(tabList => {
      // 检查是否需要滚动
      function checkScroll() {
        const activeTab = tabList.querySelector('.nav-link.active');
        if (activeTab && tabList.scrollWidth > tabList.clientWidth) {
          // 将活动标签滚动到可见区域
          const tabRect = activeTab.getBoundingClientRect();
          const listRect = tabList.getBoundingClientRect();
          
          if (tabRect.left < listRect.left || tabRect.right > listRect.right) {
            activeTab.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center'
            });
          }
        }
      }

      // 监听窗口大小变化
      window.addEventListener('resize', checkScroll);
      
      // 初始检查
      checkScroll();
    });
  }

  /**
   * 动态内容加载支持
   */
  function setupDynamicContent() {
    document.addEventListener('show.bs.tab', (e) => {
      const tab = e.target;
      const targetSelector = tab.getAttribute('data-bs-target') || 
                            tab.getAttribute('href');
      const target = document.querySelector(targetSelector);
      
      // 检查是否需要加载内容
      if (target && target.hasAttribute('data-bs-load')) {
        const url = target.getAttribute('data-bs-load');
        
        if (!target.hasAttribute('data-loaded')) {
          // 显示加载状态
          target.innerHTML = '<div class="text-center py-5"><div class="spinner-border text-primary" role="status"><span class="visually-hidden">加载中...</span></div></div>';
          
          // 加载内容
          fetch(url)
            .then(response => response.text())
            .then(html => {
              target.innerHTML = html;
              target.setAttribute('data-loaded', 'true');
            })
            .catch(error => {
              console.error('Failed to load tab content:', error);
              target.innerHTML = '<div class="alert alert-danger">加载失败，请重试。</div>';
            });
        }
      }
    });
  }

  // 监听动态添加的标签页元素
  function observeNewTabs() {
    const observer = new MutationObserver((mutations) => {
      let hasNewTabs = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // 检查新添加的节点是否包含标签页元素
            const newTabs = node.querySelectorAll ? 
              node.querySelectorAll('[data-bs-toggle="tab"], [data-bs-toggle="pill"]') : [];
            if (newTabs.length > 0) {
              hasNewTabs = true;
            }
            // 检查节点本身是否是标签页元素
            if (node.matches && node.matches('[data-bs-toggle="tab"], [data-bs-toggle="pill"]')) {
              hasNewTabs = true;
            }
          }
        });
      });
      
      if (hasNewTabs) {
        // 重新初始化（只会初始化新的元素）
        initTabs();
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // DOM 加载完成后初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initTabs();
      setupResponsive();
      setupDynamicContent();
      observeNewTabs();
    });
  } else {
    initTabs();
    setupResponsive();
    setupDynamicContent();
    observeNewTabs();
  }

  // 暴露到全局
  window.UITabs = {
    Tab,
    init: initTabs
  };

  // jQuery 插件（如果 jQuery 可用）
  if (typeof jQuery !== 'undefined') {
    (function($) {
      $.fn.tab = function(option) {
        return this.each(function() {
          const $this = $(this);
          let data = $this.data('ui.tab');
          const options = typeof option === 'object' && option;

          if (!data) {
            data = new Tab(this, options);
            $this.data('ui.tab', data);
          }

          if (typeof option === 'string') {
            if (typeof data[option] === 'undefined') {
              throw new TypeError(`No method named "${option}"`);
            }
            data[option]();
          }
        });
      };

      $.fn.tab.Constructor = Tab;
    })(jQuery);
  }

})();
